/**
 * Excel date arithmetic on serial numbers, used to compute expected answers.
 * Each helper mirrors the Excel function named in its comment.
 */

const EPOCH = Date.UTC(1899, 11, 30);
const DAY_MS = 86400000;

export function ymd(serial: number): { y: number; m: number; d: number } {
  const date = new Date(EPOCH + Math.floor(serial) * DAY_MS);
  return { y: date.getUTCFullYear(), m: date.getUTCMonth() + 1, d: date.getUTCDate() };
}

/** FECHA(año, mes, día) — months and days may overflow, like Excel. */
export function fecha(y: number, m: number, d: number): number {
  return (Date.UTC(y, m - 1, d) - EPOCH) / DAY_MS;
}

/** DIASEM(fecha, 2): lunes = 1 … domingo = 7. */
export function diasem2(serial: number): number {
  const day = new Date(EPOCH + Math.floor(serial) * DAY_MS).getUTCDay();
  return ((day + 6) % 7) + 1;
}

/** FIN.MES(fecha, meses) */
export function finMes(serial: number, meses: number): number {
  const { y, m } = ymd(serial);
  return fecha(y, m + meses + 1, 0);
}

/** FECHA.MES(fecha, meses) — clamps to the last day when the month is shorter. */
export function fechaMes(serial: number, meses: number): number {
  const { y, m, d } = ymd(serial);
  const ultimo = ymd(fecha(y, m + meses + 1, 0)).d;
  return fecha(y, m + meses, Math.min(d, ultimo));
}

/** SIFECHA(inicio, fin, "Y" | "YM" | "M") */
export function sifecha(inicio: number, fin: number, unidad: "Y" | "YM" | "M"): number {
  const a = ymd(inicio);
  const b = ymd(fin);
  const meses = (b.y - a.y) * 12 + (b.m - a.m) - (b.d < a.d ? 1 : 0);
  if (unidad === "M") return meses;
  if (unidad === "Y") return Math.floor(meses / 12);
  return meses % 12;
}

/** Weekend mask as DIAS.LAB.INTL strings: 7 characters lunes→domingo, "1" = descanso. */
export const SAB_DOM = "0000011";
const esLaborable = (serial: number, mask: string, festivos: Set<number>) =>
  mask[diasem2(serial) - 1] === "0" && !festivos.has(serial);

/** DIAS.LAB / DIAS.LAB.INTL — counts both ends; negative when fin < inicio. */
export function diasLab(inicio: number, fin: number, festivos: number[] = [], mask = SAB_DOM): number {
  const hol = new Set(festivos);
  const [a, b, sign] = inicio <= fin ? [inicio, fin, 1] : [fin, inicio, -1];
  let count = 0;
  for (let s = a; s <= b; s++) if (esLaborable(s, mask, hol)) count++;
  return sign * count;
}

/** DIA.LAB / DIA.LAB.INTL — the start date itself is not counted. */
export function diaLab(inicio: number, dias: number, festivos: number[] = [], mask = SAB_DOM): number {
  const hol = new Set(festivos);
  let s = inicio;
  let restantes = dias;
  while (restantes > 0) {
    s++;
    if (esLaborable(s, mask, hol)) restantes--;
  }
  return s;
}

/** Días festivos oficiales de México 2026 (LFT, art. 74). */
export const FESTIVOS_2026: [number, string][] = [
  [fecha(2026, 1, 1), "Año Nuevo"],
  [fecha(2026, 2, 2), "Día de la Constitución"],
  [fecha(2026, 3, 16), "Natalicio de Benito Juárez"],
  [fecha(2026, 5, 1), "Día del Trabajo"],
  [fecha(2026, 9, 16), "Día de la Independencia"],
  [fecha(2026, 11, 16), "Día de la Revolución"],
  [fecha(2026, 12, 25), "Navidad"],
];
